import React, { createContext, useContext, useState, useEffect } from 'react';
import {
    getFontSizeSetting,
    setFontSizeSetting,
    getScaledFontSize,
    getAllScaledFontSizes
} from '../utils/fontSizeManager';

// Create the context
const FontSizeContext = createContext();

// Custom hook to use the font size context
export const useFontSize = () => {
    const context = useContext(FontSizeContext);
    if (!context) {
        throw new Error('useFontSize must be used within a FontSizeProvider');
    }
    return context;
};

// Provider component
export const FontSizeProvider = ({ children }) => {
    const [currentSetting, setCurrentSetting] = useState('medium');
    const [scaledSizes, setScaledSizes] = useState({});
    const [updateKey, setUpdateKey] = useState(0);

    // Update scaled sizes when setting changes
    const updateScaledSizes = () => {
        const newScaledSizes = getAllScaledFontSizes();
        setScaledSizes(newScaledSizes);
        setUpdateKey(prev => prev + 1); // Force re-render of components
    };

    // Initialize font size setting
    useEffect(() => {
        const setting = getFontSizeSetting();
        setCurrentSetting(setting);
        updateScaledSizes();
    }, []);

    // Change font size setting
    const changeFontSize = (newSize) => {
        // console.log('FontSizeContext - changeFontSize called with:', newSize);
        setFontSizeSetting(newSize);
        setCurrentSetting(newSize);
        updateScaledSizes();
        // console.log('FontSizeContext - after update, currentSetting:', newSize);
    };

    // Get scaled font size for a specific text type
    const getFontSize = (textType) => {
        const size = getScaledFontSize(textType);
        // console.log(`FontSizeContext - getFontSize(${textType}) = ${size}, currentSetting: ${currentSetting}`);
        return size;
    };

    // Create dynamic styles with current font sizes
    const createDynamicStyles = (styleMap) => {
        const styles = {};
        for (const [key, textType] of Object.entries(styleMap)) {
            styles[key] = {
                fontSize: getScaledFontSize(textType)
            };
        }
        return styles;
    };

    const value = {
        currentSetting,
        scaledSizes,
        updateKey,
        changeFontSize,
        getFontSize,
        createDynamicStyles
    };

    // console.log('FontSizeContext Provider - value:', value);

    return (
        <FontSizeContext.Provider value={value}>
            {children}
        </FontSizeContext.Provider>
    );
};
